import { FuelUnit } from '@fe/core';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Checkbox, ColorPickerChangeEvent, TextArea, TextAreaChangeEvent, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { clipboardIcon } from '@progress/kendo-svg-icons';
import React from 'react';
import { ColorPickerWithDefault } from '../../app/components/inputs/ColorPickerWithDefault';
import { useMultiWindowContext } from '../../app/components/multi-window/context/multi-window-context';
import { useAppDispatch, useAppSelector } from '../../app/hooks/store-hooks';
import { NewRouteModel, routeAnalyzerAddRoutes, routeAnalyzerUpdateRoute } from '../../app/state/slices/route-analyzer-slice';
import Fuel from '../../common/fuel';
import { DateTimeFormatter, UnitFormatter } from '../../formatters';
import { AddOutline, EditOutline, TrashOutline } from '../../icons/icons';
import EditPointRing from './EditPointRing';
import { staticRouteColors } from './static-colors';
import { AnalyzerRoute, AnalyzerRouteAssociatedPoint, AnalyzerRouteType } from './types';
export interface AddAdHocRouteProps {
    editItem?: AnalyzerRoute;
    onClose: () => void;
}

const EditRoute = (props: AddAdHocRouteProps) => {
    const [label, setLabel] = React.useState('');
    const [color, setColor] = React.useState('');
    const [initialColor, setInitialColor] = React.useState('');
    const [origin, setOrigin] = React.useState('');
    const [destination, setDestination] = React.useState('');
    const [sequence, setSequence] = React.useState('');
    const [route, setRoute] = React.useState('');
    const [associatedPoints, setAssociatedPoints] = React.useState<AnalyzerRouteAssociatedPoint[]>([]);
    const [editPointVisible, setEditPointVisible] = React.useState(false);
    const [editPoint, setEditPoint] = React.useState<AnalyzerRouteAssociatedPoint | undefined>(undefined);
    const multiWindowContext = useMultiWindowContext();
    const autogeneratedColorIndex = useAppSelector((x) => x.routeAnalyzer.autogeneratedColorIndex);

    const handleLabelChange = React.useCallback((event: TextBoxChangeEvent) => setLabel(event.value as string), []);
    const handleColorChange = React.useCallback((event: ColorPickerChangeEvent) => setColor(event.value), []);
    const handleOriginChange = React.useCallback((event: TextBoxChangeEvent) => setOrigin(event.value as string), []);
    const handleDestinationChange = React.useCallback((event: TextBoxChangeEvent) => setDestination(event.value as string), []);
    const handleSequenceChange = React.useCallback((event: TextBoxChangeEvent) => setSequence(event.value as string), []);
    const handleRouteChange = React.useCallback((event: TextAreaChangeEvent) => setRoute(event.value as string), []);
    const handlePaste = React.useCallback(async () => {
        const text = await navigator.clipboard.readText();
        if (text) {
            setRoute(text);
        }
    }, []);
    const dispatch = useAppDispatch();

    React.useEffect(() => {
        if (props.editItem) {
            setLabel(props.editItem.label);
            setColor(props.editItem.color);
            setInitialColor(props.editItem.color);
            setOrigin(props.editItem.origin);
            setDestination(props.editItem.destination);
            setSequence(props.editItem.sequence);
            setRoute(props.editItem.route);
            setAssociatedPoints([...props.editItem.associatedPoints]);
        } else {
            const generatedColor = staticRouteColors[autogeneratedColorIndex % staticRouteColors.length];
            setColor(generatedColor);
            setInitialColor(generatedColor);
        }
    }, [props.editItem, autogeneratedColorIndex]);

    const handleAdd = async () => {
        // Modified
        if (props.editItem) {
            const editedRoute = {
                ...props.editItem,
                label: label,
                origin: origin,
                destination: destination,
                sequence: sequence,
                color: color,
                associatedPoints: associatedPoints,
                route: route
            };
            dispatch(routeAnalyzerUpdateRoute({ route: editedRoute }));
        }
        // New added
        else {
            const newRoute: NewRouteModel = {
                type: AnalyzerRouteType.adhoc,
                label: label,
                origin: origin,
                destination: destination,
                sequence: sequence,
                distance: 0,
                ete: null,
                time: null,
                color: color,
                route: route,
                associatedPoints: associatedPoints,
                waypoints: null,
                feedId: null,
                visible: true
            };
            dispatch(routeAnalyzerAddRoutes({ routes: [newRoute] }));
        }

        props.onClose();
    };

    const handleCancel = () => {
        props.onClose();
    };

    const handleAddPoint = () => {
        setEditPoint(undefined);
        setEditPointVisible(true);
    };

    const handleEditPointSave = (item: AnalyzerRouteAssociatedPoint) => {
        if (editPoint) {
            const idx = associatedPoints.indexOf(editPoint);
            if (idx >= 0) {
                setAssociatedPoints([...associatedPoints.slice(0, idx), item, ...associatedPoints.slice(idx + 1)]);
            }
        } else {
            setAssociatedPoints((p) => [...p, item]);
        }
    };

    const handleRemovePoint = (item: AnalyzerRouteAssociatedPoint) => {
        setAssociatedPoints((p) => p.filter((x) => x !== item));
    };

    const handleEditPoint = (item: AnalyzerRouteAssociatedPoint) => {
        setEditPoint(item);
        setEditPointVisible(true);
    };

    const togglePoint = (item: AnalyzerRouteAssociatedPoint) => {
        item.enabled = !item.enabled;
        setAssociatedPoints((points) => [...points]);
    };

    const fuelUnit = props.editItem?.waypoints?.fuelUnit ?? FuelUnit.Null;

    return (
        <div>
            <Dialog
                appendTo={multiWindowContext.root}
                width={'min(1000px, 100vw)'}
                height={'min(1000px, 100vh)'}
                title={`${props.editItem ? 'Edit' : 'Add'} route`}
                onClose={handleCancel}>
                <div className="h-100" style={{ display: 'grid', gridTemplateRows: '1.618fr auto 1fr', minHeight: 800 }}>
                    <div className="d-flex gap-2" style={{ minHeight: 0 }}>
                        <div className="d-flex flex-column w-50">
                            <div className="secondary-text">Label</div>
                            <div className="d-flex gap-2">
                                <TextBox placeholder="Label" value={label} onChange={handleLabelChange} />
                                <div className="flex-shrink-0">
                                    <ColorPickerWithDefault
                                        defaultColor={initialColor}
                                        value={color}
                                        views={['palette', 'gradient']}
                                        onChange={handleColorChange}
                                    />
                                </div>
                            </div>
                            <div className="secondary-text pt-2">Origin</div>
                            <TextBox className="input-uppercase" placeholder="Origin" value={origin} onChange={handleOriginChange} />

                            <div className="secondary-text pt-2">Destination</div>
                            <TextBox
                                className="input-uppercase"
                                placeholder="Destination"
                                value={destination}
                                onChange={handleDestinationChange}
                            />

                            <div className="secondary-text pt-2">Sequence #</div>
                            <TextBox placeholder="Sequence #" value={sequence} onChange={handleSequenceChange} />

                            <div className="secondary-text pt-2">Route text</div>
                            <TextArea
                                style={{ height: '160px', width: '100%' }}
                                className="flex-grow-1"
                                resizable={'none'}
                                placeholder="Route text"
                                value={route}
                                onChange={handleRouteChange}
                            />
                            <Button className="mt-2" svgIcon={clipboardIcon} fillMode={'outline'} onClick={handlePaste}>
                                Paste from clipboard
                            </Button>
                        </div>
                        <div className="w-50 border-outline p-2">
                            <div className="overflow-auto h-100">
                                <table className="cobalt-table cobalt-alternate w-100">
                                    <thead>
                                        <tr style={{ position: 'sticky', top: -1, height: 30, backgroundColor: 'var(--alternate-color)' }}>
                                            <th className="ps-2">Waypoint</th>
                                            <th>Fuel on board</th>
                                            <th>Altitude</th>
                                            <th>Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {props.editItem?.waypoints?.items != null &&
                                            props.editItem.waypoints.items.map((item, i) => {
                                                return (
                                                    <tr key={i}>
                                                        <td className="ps-2">{item.id}</td>
                                                        <td>{item.fob ? UnitFormatter.formatFuel(new Fuel(item.fob, fuelUnit)) : '-'}</td>
                                                        <td>{item.at ?? '-'}</td>
                                                        <td>{item.ete ? DateTimeFormatter.formatTime(item.ete) : '-'}</td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div className="mt-2 secondary-text d-flex gap-2 py-2 align-items-center">
                        <Button
                            svgIcon={AddOutline}
                            className="button-small"
                            fillMode={'outline'}
                            size={'small'}
                            onClick={handleAddPoint}></Button>
                        <div className="flex-grow-1">Associated points/rings</div>
                        <Button
                            svgIcon={TrashOutline}
                            className="button-small"
                            fillMode={'outline'}
                            size={'small'}
                            onClick={() => setAssociatedPoints([])}></Button>
                    </div>

                    <div className="w-100 border-outline p-2" style={{ minHeight: 0 }}>
                        <div className="overflow-auto h-100">
                            <table className="cobalt-table cobalt-alternate w-100">
                                <thead>
                                    <tr
                                        style={{
                                            position: 'sticky',
                                            top: -1,
                                            height: 30,
                                            backgroundColor: 'var(--alternate-color)',
                                            zIndex: 2
                                        }}>
                                        <th className="ps-2">Enable</th>
                                        <th>Label</th>
                                        <th>Latitude</th>
                                        <th>Longitude</th>
                                        <th>Range</th>
                                        <th>Color</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {associatedPoints.map((item, i) => {
                                        return (
                                            <tr key={i}>
                                                <td className="ps-2">
                                                    <Checkbox value={item.enabled} onChange={() => togglePoint(item)} />
                                                </td>
                                                <td>{item.label || '-'}</td>
                                                <td>{item.latitude.toFixed(5)}</td>
                                                <td>{item.longitude.toFixed(5)}</td>
                                                <td>{item.range}</td>
                                                <td>
                                                    <div style={{ background: item.color, height: '20px', aspectRatio: '1/1' }}></div>
                                                </td>
                                                <td className="d-flex gap-1 text-end justify-content-end align-items-center">
                                                    <Button
                                                        svgIcon={EditOutline}
                                                        fillMode={'outline'}
                                                        className="button-small"
                                                        onClick={() => handleEditPoint(item)}
                                                    />
                                                    <Button
                                                        svgIcon={TrashOutline}
                                                        fillMode={'outline'}
                                                        className="button-small"
                                                        onClick={() => handleRemovePoint(item)}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <DialogActionsBar layout={'stretched'}>
                    <div className="d-flex gap-2 px-3">
                        <Button onClick={handleAdd} disabled={!label || !route} className="w-50" themeColor={'primary'}>
                            {props.editItem ? 'Save' : 'Add'}
                        </Button>
                        <Button onClick={handleCancel} className="w-50" fillMode={'outline'}>
                            Cancel
                        </Button>
                    </div>
                </DialogActionsBar>
            </Dialog>
            {editPointVisible && (
                <EditPointRing editItem={editPoint} onSave={handleEditPointSave} onClose={() => setEditPointVisible(false)} />
            )}
        </div>
    );
};

export default EditRoute;
